import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { mergeMap, tap } from 'rxjs/operators';
import {
  VendorFormatService,
  ClosableModalService,
  BookmarkAPI,
  mapVendorProfileProcessSubprocess,
  SupplierListModel,
  IBookmark,
  IFavorite
} from 'sustainment-component';
import { CapabilityActions } from '../capability/capability.action';
import { BookmarkStore } from './bookmark.store';
import { CapabilityQuery } from '../capability/capability.query';

@Injectable({ providedIn: 'root' })
export class BookmarkActions {
  public constructor(
    private _api: BookmarkAPI,
    private _store: BookmarkStore,
    private _closeableModals: ClosableModalService,
    private _capabilityAction: CapabilityActions,
    private _vendorFormatService: VendorFormatService,
    private _capabilityQuery: CapabilityQuery
  ) {}

  public getBookmarks(): void {
    this._store.setLoading(true);
    this._store.update({ bookmarks: [] });
    this._capabilityQuery.select().subscribe((capabilities) => {
      this._api
        .getBookmarks()
        .pipe(this._addLogos)
        .subscribe((results: IBookmark[]) => {
          this._store.update({ bookmarks: results || [] });

          results.forEach((bookmark) => {
            bookmark.favorites?.forEach(({ ...favorite }) => {
              if (favorite.vendor) {
                favorite.vendor = { ...favorite.vendor };

                favorite.vendor =
                  this._capabilityAction.mapVendorProfileIndustries(
                    favorite.vendor
                  );

                if (favorite.vendor?.processes && capabilities?.processList)
                  favorite.vendor.processes = mapVendorProfileProcessSubprocess(
                    favorite.vendor,
                    capabilities.processList
                  );
              }
            });
          });

          this._store.setLoading(false);
        });
    });
  }

  public setBookmarks(results: IBookmark[]): void {
    this._capabilityQuery.select().subscribe((capabilities) => {
      this._store.update({ bookmarks: results || [] });

      results.forEach((bookmark) => {
        bookmark.favorites?.forEach(({ ...favorite }) => {
          if (favorite.vendor) {
            favorite.vendor = { ...favorite.vendor };

            favorite.vendor = this._capabilityAction.mapVendorProfileIndustries(
              favorite.vendor
            );

            if (favorite.vendor?.processes && capabilities?.processList)
              favorite.vendor.processes = mapVendorProfileProcessSubprocess(
                favorite.vendor,
                capabilities.processList
              );
          }
        });
      });
    });
  }

  public addVendorToBookmark(
    bookmark: IBookmark,
    vendor: SupplierListModel
  ): Observable<unknown> {
    const existingBookmark = this._store
      .getValue()
      .bookmarks.find((y) => y.collectionId === bookmark.collectionId);
    if (
      !existingBookmark ||
      existingBookmark.favorites?.some(
        (f) => f.vendor?.sustainmentId === vendor.sustainmentId
      )
    ) {
      return of({});
    }

    return this._api.addVendorToBookmark(bookmark, vendor.sustainmentId).pipe(
      tap(() => {
        this._store.setLoading(false);
        this.getBookmarks();
      })
    );
  }

  /* eslint-disable */
  public deleteVendorToBookmark(favorite: IFavorite): Observable<unknown> {
    return this._api
      .deleteVendorToBookmark(favorite.favoriteId?.toString()!)
      .pipe(
        tap(() => {
          this._store.setLoading(false);
          const currentState = this._store.getValue();
          
          const updatedBookmarks = currentState.bookmarks.map(bookmark => {
            if (bookmark.favorites?.some(f => f.favoriteId === favorite.favoriteId)) {
              const updatedBookmark = {
                ...bookmark,
                favorites: bookmark.favorites.filter(f => f.favoriteId !== favorite.favoriteId)
              };
              return updatedBookmark;
            }
            return bookmark;
          });
          
          this._store.update({ bookmarks: updatedBookmarks });
        })
      );
  }

  public deleteVendorFromBookmark(
    sustainmentId: string,
    bookmarkId: string
  ): Observable<unknown> {
    const state = this._store.getValue();
    
    const bookmark = state.bookmarks.find((bkmrk) => bkmrk.collectionId?.toString() === bookmarkId);
    if (!bookmark) {
      return of();
    }
    
    const favorite = bookmark.favorites?.find(
      (fave) => fave.companySustainmentId === sustainmentId
    );
    if (!favorite) {
      return of();
    }
    
    return this.deleteVendorToBookmark(favorite);
  }

  public createBookmark(bookmark: IBookmark): void {
    this._api
      .createBookmark(bookmark)
      .pipe(
        tap((newData) => {
          const oldValue = this._store.getValue();
          const bookmarks = oldValue.bookmarks
            ? oldValue.bookmarks.slice(0, oldValue.bookmarks.length)
            : [];
          bookmarks.push(newData);
          this._store.update({ bookmarks });
          this._store.setLoading(false);
          this._closeableModals.close();
        })
      )
      .subscribe(() => this._closeableModals.reset());
  }

  public updateBookmark(bookmark: IBookmark): void {
    this._api
      .updateBookmark(bookmark)
      .pipe(
        tap((newData) => {
          const oldValue = this._store.getValue();
          const bookmarks = oldValue.bookmarks.map((b) => {
            if (b.collectionId === bookmark.collectionId) {
              return {
                ...b,
                name: newData.name,
                description: newData.description
              };
            }
            return b;
          });
          this._store.update({ bookmarks });
          this._store.setLoading(false);
          this._closeableModals.close();
        })
      )
      .subscribe(() => this._closeableModals.reset());
  }

  public deleteBookmark(bookmark: IBookmark): Observable<any> {
    return this._api
      .deleteBookmark(bookmark)
      .pipe(
        tap(() => {
          this._store.update((state) => {
            const bookmarks = state.bookmarks.filter(
              (b) => b.collectionId !== bookmark.collectionId
            );
            return { ...state, bookmarks };
          });
          this._store.setLoading(false);
          this._closeableModals.close();
        }),
        tap(() => {
          this._closeableModals.reset();
        })
      );
  }

  public updateNoteValue(
    companySustainmentId: string,
    notesCount: number
  ): void {
    this._store.setLoading(true);
    const oldValue = this._store.getValue();

    if (!oldValue.bookmarks) {
      return;
    }

    const results = oldValue.bookmarks.map((b: IBookmark) => {
      if (
        b.favorites?.filter(
          (v) => v.companySustainmentId === companySustainmentId
        ).length
      ) {
        return {
          ...b,
          favorites: b.favorites.map((f) => {
            if (f.companySustainmentId === companySustainmentId) {
              return { ...f, vendor: { ...f.vendor, userNotes: notesCount } };
            }
            return f;
          })
        };
      }
      return b;
    });
    this._store.update({ bookmarks: results as IBookmark[] });
    this._store.setLoading(false);
  }

  public setConnectionRequested(sustainmentId: string): void {
    const values = this._store.getValue().bookmarks;
    if (!values) return;

    const bookmarks = values.map((b: IBookmark) => {
      if (
        b.favorites?.filter((v) => v.vendor?.sustainmentId === sustainmentId)
          .length
      ) {
        return {
          ...b,
          favorites: b.favorites.map((f) => {
            if (f.vendor?.sustainmentId === sustainmentId) {
              return {
                ...f,
                vendor: { ...f.vendor, connectionRequested: true }
              };
            }
            return f;
          })
        };
      }
      return b;
    });

    this._store.update({ bookmarks });
  }

  public setInvited(companySustainmentId: string): void {
    const values = this._store.getValue().bookmarks;
    if (!values) return;

    const bookmarks = values.map((b) => {
      if (
        b.favorites?.filter(
          (v) => v.companySustainmentId === companySustainmentId
        ).length
      ) {
        return {
          ...b,
          favorites: b.favorites.map((f) => {
            if (f.vendor && f.companySustainmentId === companySustainmentId) {
              return { ...f, vendor: { ...f.vendor, inviteRequested: true } };
            }
            return f;
          })
        };
      }
      return b;
    });

    this._store.update({ bookmarks });
  }

  private _addLogos = mergeMap((results: IBookmark[]) => {
    results.forEach((bookmark) => {
      if (bookmark.favorites && bookmark?.favorites?.length > 0)
        bookmark.favorites.map((r) => {
          if (r.vendor)
            r.vendor.logoUrl = this._vendorFormatService.getLogo(
              r?.vendor?.sustainmentId,
              r.vendor?.logo || ''
            );
        });
    });
    return of(results);
  });
}
